import React, { useEffect, useRef, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import Select from "react-select";
import Swal from "sweetalert2";
import Loader from "../Loader/loader";
import CashEntryDetailsModal from "./CashEntryDetailsModal";
import CashDocumentPreviewModal, {
  getCashDocuments
} from "./CashDocumentPreviewModal";
import CashPassbookViewerServices from "../../../services/CashPassbookViewerServices";
import Pagination from "../Pagenation/Pagenation";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const readData = (response) => {
  const body = response?.data?.status ? response.data : response;
  return body?.data;
};

const toInitCap = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const getAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const formatEntryBalance = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return formatAmount(value);
};

const formatAmount = (value) =>
  getAmount(value).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const formatDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts.map(({ type, value: partValue }) => [type, partValue])
  );

  return {
    date: `${values.day} ${values.month}, ${values.year}`,
    time: `${values.hour}:${values.minute} ${values.dayPeriod.toUpperCase()}`
  };
};

const statusLabels = {
  pendingAdmin: "Pending with Project Manager",
  pendingAccounts: "Pending with Accounts Team",
  approved: "Completed",
  rejectedByAdmin: "Rejected by Project Manager",
  rejectedByAccounts: "Rejected by Accounts Team"
};

const statusColors = {
  pendingAdmin: "bg-warning text-dark",
  pendingAccounts: "bg-primary",
  approved: "bg-success",
  rejectedByAdmin: "bg-danger",
  rejectedByAccounts: "bg-danger"
};

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    fontSize: "13px"
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20
  })
};

const emptyPagination = {
  total: 0,
  totalPages: 1
};

const showError = (error, fallback) =>
  Swal.fire(
    "Error",
    error?.response?.data?.message || error?.message || fallback,
    "error"
  );

const SectionToggle = ({ open, onClick, controls, label }) => (
  <button
    type="button"
    className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
    onClick={onClick}
    aria-expanded={open}
    aria-controls={controls}
    aria-label={`${open ? "Hide" : "Show"} ${label}`}
    title={`${open ? "Hide" : "Show"} ${label}`}
    style={{
      flexShrink: 0,
      whiteSpace: "nowrap",
      fontSize: "12px",
      padding: "5px 10px"
    }}
  >
    <i
      className={`fas ${open ? "fa-angle-up" : "fa-angle-down"}`}
      aria-hidden="true"
    />
    {open ? "Hide" : "Show"}
  </button>
);


const INDIGO_LOGO_URL =
  "https://indigoinfra.in/img/product/product-1739471679615.jpeg";

// Convert company logo to PDF-compatible image data.
const getPdfLogo = async () => {
  const response = await fetch(INDIGO_LOGO_URL, {
    mode: "cors"
  });

  if (!response.ok) {
    throw new Error("Unable to load company logo.");
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = () =>
      reject(new Error("Unable to read company logo."));

    reader.readAsDataURL(blob);
  });
};


const CashPassbookViewer = ({
  embedded = false,
  initialStoreId = "",
  initialPassbookId = ""
}) => {
  const [searchOpen, setSearchOpen] = useState(!embedded);
  const [dataOpen, setDataOpen] = useState(embedded);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [workflowRefresh, setWorkflowRefresh] = useState(0);

  const [selectedBills, setSelectedBills] = useState(null);
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(0);

  const [stores, setStores] = useState([]);
  const [passbooks, setPassbooks] = useState([]);
  const [storeId, setStoreId] = useState(initialStoreId);
  const [passbookId, setPassbookId] = useState(initialPassbookId);

  const [passbook, setPassbook] = useState(null);
  const [projection, setProjection] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    setSelectedEntry((previous) => {
      if (!previous) return null;

      const updatedEntry = entries.find(
        (item) => String(item._id) === String(previous._id)
      );

      return updatedEntry || previous;
    });
  }, [entries]);

  const [storesLoading, setStoresLoading] = useState(false);
  const [passbooksLoading, setPassbooksLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const [entryType, setEntryType] = useState("");
  const [status, setStatus] = useState("");

  // Duration filter
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [vendorId, setVendorId] = useState("");

  const [categoryFilters, setCategoryFilters] = useState([]);
  const [vendorFilters, setVendorFilters] = useState([]);

  // Existing Pagination uses zero-based pages.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(emptyPagination);

  // Automatically change sections only when a newly selected
  // passbook loads, not on every filter/page/refresh request.
  const revealSelectedPassbook = useRef(false);

  useEffect(() => {
    if (embedded) return;
    let cancelled = false;

    const loadStores = async () => {
      setStoresLoading(true);

      try {
        const response = await CashPassbookViewerServices.getStores();
        const data = readData(response);

        if (!Array.isArray(data?.stores)) {
          throw new Error("Unexpected store API response.");
        }

        if (!cancelled) {
          setStores(data.stores);
        }
      } catch (error) {
        if (!cancelled) {
          showError(error, "Unable to load stores.");
        }
      } finally {
        if (!cancelled) setStoresLoading(false);
      }
    };

    loadStores();

    return () => {
      cancelled = true;
    };
  }, [embedded]);

  useEffect(() => {
    if (embedded) return;
    let cancelled = false;

    setPassbooks([]);

    if (!storeId) {
      setPassbooksLoading(false);
      return;
    }

    const loadPassbooks = async () => {
      setPassbooksLoading(true);

      try {
        const response =
          await CashPassbookViewerServices.getPassbooks(storeId);
        const data = readData(response);

        if (!Array.isArray(data?.passbooks)) {
          throw new Error("Unexpected passbook API response.");
        }

        if (!cancelled) {
          setPassbooks(data.passbooks);
        }
      } catch (error) {
        if (!cancelled) {
          showError(error, "Unable to load passbooks.");
        }
      } finally {
        if (!cancelled) setPassbooksLoading(false);
      }
    };

    loadPassbooks();

    return () => {
      cancelled = true;
    };
  }, [storeId, embedded]);

  useEffect(() => {
    let cancelled = false;

    if (!storeId || !passbookId) {
      setDetailsLoading(false);
      return;
    }

    const loadDetails = async () => {
      setDetailsLoading(true);
      setDetailsError("");

      try {
        const response =
          await CashPassbookViewerServices.getPassbookDetails(
            passbookId,
            {
              storeId,
              entryType,
              status,
              categoryId,
              vendorId,
              search,
              fromDate: appliedFromDate,
              toDate: appliedToDate,
              page: page + 1,
              limit: pageSize
            }
          );

        const data = readData(response);

        if (
          !data?.passbook ||
          !Array.isArray(data?.entries) ||
          !data?.pagination
        ) {
          throw new Error("Unexpected passbook details API response.");
        }

        if (!cancelled) {
          setPassbook(data.passbook);
          setProjection(data.projection || null);
          setEntries(data.entries);
          setCategoryFilters(data.filterOptions?.categories || []);
          setVendorFilters(data.filterOptions?.vendors || []);
          setPagination(data.pagination);

          const returnedPage = data.pagination.page - 1;

          if (returnedPage !== page) {
            setPage(returnedPage);
          }

          if (revealSelectedPassbook.current) {
            setSearchOpen(false);
            setDataOpen(true);
            revealSelectedPassbook.current = false;
          }
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load passbook details.";

          setPassbook(null);
          setProjection(null);
          setEntries([]);
          setPagination(emptyPagination);
          setDetailsError(message);
          setSearchOpen(true);

          showError(error, "Unable to load passbook details.");
        }
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [
    storeId,
    passbookId,
    entryType,
    status,
    categoryId,
    vendorId,
    search,
    appliedFromDate,
    appliedToDate,
    page,
    pageSize,
    workflowRefresh
  ]);

  const clearDetails = () => {
    setPassbook(null);
    setProjection(null);
    setEntries([]);
    setDetailsError("");
    setPagination(emptyPagination);
    setPage(0);
  };

  const changeStore = (selected) => {
    revealSelectedPassbook.current = false;

    setStoreId(selected?.value || "");
    setPassbookId("");
    setPassbooks([]);
    setEntryType("");
    setStatus("");
    setSearchText("");
    setSearch("");
    setCategoryId("");
    setVendorId("");

    setFromDate("");
    setToDate("");
    setAppliedFromDate("");
    setAppliedToDate("");

    setCategoryFilters([]);
    setVendorFilters([]);

    clearDetails();
    setSearchOpen(true);
    setDataOpen(false);
  };

  const changePassbook = (selected) => {
    const selectedId = selected?.value || "";

    revealSelectedPassbook.current = Boolean(selectedId);

    setPassbookId(selectedId);
    setEntryType("");
    setStatus("");
    setSearchText("");
    setSearch("");
    setCategoryId("");
    setVendorId("");

    setFromDate("");
    setToDate("");
    setAppliedFromDate("");
    setAppliedToDate("");

    setCategoryFilters([]);
    setVendorFilters([]);

    clearDetails();
    setSearchOpen(true);
    setDataOpen(false);
  };

  const storeOptions = stores.map((store) => ({
    value: String(store._id),
    label: `${toInitCap(store.name)}${store.location ? ` — ${toInitCap(store.location)}` : ""
      }`
  }));

  const passbookOptions = passbooks.map((item) => ({
    value: String(item._id),
    label: `${toInitCap(item.name)}${item.status === "inactive" ? " (Inactive)" : ""
      }`
  }));

  const formatProjectionNumber = (value) =>
    getAmount(value).toLocaleString("en-IN", {
      maximumFractionDigits: 2
    });

  const approvedIn = getAmount(passbook?.totalIn);
  const approvedOut = getAmount(passbook?.totalOut);

  // These API values contain pending and rejected amounts only.
  const additionalIn = getAmount(projection?.pendingIn);
  const additionalOut = getAmount(projection?.pendingOut);

  const projectedCashIn = approvedIn + additionalIn;
  const projectedCashOut = approvedOut + additionalOut;
  const projectedNetBalance = projectedCashIn - projectedCashOut;

  const balanceItems = [
    {
      key: "in",
      label: "Cash In",
      value: passbook?.totalIn,
      symbol: "+",
      className: "cash-passbook-icon-in",
      projectionCalculation:
        `${formatProjectionNumber(approvedIn)} + ` +
        `${formatProjectionNumber(additionalIn)} = ` +
        `${formatProjectionNumber(projectedCashIn)}`
    },
    {
      key: "out",
      label: "Cash Out",
      value: passbook?.totalOut,
      symbol: "−",
      className: "cash-passbook-icon-out",
      projectionCalculation:
        `${formatProjectionNumber(approvedOut)} + ` +
        `${formatProjectionNumber(additionalOut)} = ` +
        `${formatProjectionNumber(projectedCashOut)}`
    },
    {
      key: "balance",
      label: "Net Balance",
      value: passbook?.balance,
      symbol: "=",
      className: "cash-passbook-icon-balance",
      projectionCalculation:
        `${formatProjectionNumber(projectedCashIn)} − ` +
        `${formatProjectionNumber(projectedCashOut)} = ` +
        `${formatProjectionNumber(projectedNetBalance)}`
    }
  ];


  // ==========================================
  // DOWNLOAD COMPLETE PASSBOOK PDF
  // ==========================================
  const downloadPassbookPDF = async () => {
    if (
      !storeId ||
      !passbookId ||
      !passbook ||
      pdfDownloading ||
      detailsLoading
    ) {
      return;
    }

    setPdfDownloading(true);

    try {
      const allEntries = [];
      const exportLimit = 100;
      let exportPage = 1;
      let totalPages = 1;
      let exportPassbook = passbook;
      let exportProjection = projection;

      // Fetch all pages with current applied filters.
      do {
        const response =
          await CashPassbookViewerServices.getPassbookDetails(
            passbookId,
            {
              storeId,
              entryType,
              status,
              categoryId,
              vendorId,
              search,
              fromDate: appliedFromDate,
              toDate: appliedToDate,
              page: exportPage,
              limit: exportLimit
            }
          );

        const data = readData(response);

        if (
          !data?.passbook ||
          !Array.isArray(data?.entries) ||
          !data?.pagination
        ) {
          throw new Error("Invalid passbook export response.");
        }

        if (exportPage === 1) {
          exportPassbook = data.passbook;
          exportProjection = data.projection || null;
        }

        allEntries.push(...data.entries);

        const total = Number(data.pagination.total || 0);
        totalPages = Math.max(
          1,
          Math.ceil(total / exportLimit)
        );

        if (exportPage >= 1000) {
          throw new Error("PDF export exceeds page limit.");
        }

        exportPage += 1;
      } while (exportPage <= totalPages);

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 12;

      const pdfAmount = (value) => {
        if (
          value === null ||
          value === undefined ||
          value === ""
        ) {
          return "-";
        }

        const number = Number(value);
        if (!Number.isFinite(number)) return "-";

        return `Rs. ${number.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`;
      };

      const pdfNumber = (value) =>
        getAmount(value).toLocaleString("en-IN", {
          maximumFractionDigits: 2
        });

      const safeText = (value) =>
        String(value ?? "-")
          .replace(/[^\x20-\x7E]/g, " ")
          .trim() || "-";

      const pdfDate = (value) => {
        const formatted = formatDate(value);

        return formatted
          ? `${formatted.date} ${formatted.time}`
          : "-";
      };

      const approvedCashIn = getAmount(exportPassbook.totalIn);
      const approvedCashOut = getAmount(exportPassbook.totalOut);
      const netBalance = getAmount(exportPassbook.balance);

      const pendingCashIn = getAmount(
        exportProjection?.pendingIn
      );
      const pendingCashOut = getAmount(
        exportProjection?.pendingOut
      );

      const projectedIn = approvedCashIn + pendingCashIn;
      const projectedOut = approvedCashOut + pendingCashOut;
      const projectedBalance = projectedIn - projectedOut;


      // ==========================================
      // PDF HEADER - LOGO / PASSBOOK / PRINT DETAILS
      // ==========================================

      // Logged-in user details
      let loginDetails = {};

      try {
        loginDetails = JSON.parse(
          localStorage.getItem("userDetails") || "{}"
        );
      } catch (error) {
        loginDetails = {};
      }

      const loggedInUser =
        loginDetails?.data?.user ||
        loginDetails?.user ||
        loginDetails?.data ||
        loginDetails;

      const printedBy = safeText(
        loggedInUser?.name ||
        loggedInUser?.fullName ||
        loggedInUser?.username ||
        "Unknown"
      );

      // Current print date and time in Indian timezone
      const printDateTime = new Date();

      const printedDate = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric"
      }).format(printDateTime);

      const printedTime = new Intl.DateTimeFormat("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      }).format(printDateTime);

      // LEFT SIDE - Company logo
      try {
        const logoData = await getPdfLogo();

        const logoProps = doc.getImageProperties(logoData);

        const maxLogoWidth = 38;
        const maxLogoHeight = 23;

        const logoScale = Math.min(
          maxLogoWidth / logoProps.width,
          maxLogoHeight / logoProps.height
        );

        const logoWidth = logoProps.width * logoScale;
        const logoHeight = logoProps.height * logoScale;

        doc.addImage(
          logoData,
          "JPEG",
          margin,
          10,
          logoWidth,
          logoHeight
        );
      } catch (logoError) {
        // Continue downloading even if logo cannot load
        console.warn("PDF logo could not load:", logoError);
      }

      // CENTER - Passbook name
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(35, 45, 65);

      doc.text(
        safeText(
          toInitCap(exportPassbook.name || "Cash Passbook")
        ),
        pageWidth / 2,
        16,
        {
          align: "center",
          maxWidth: 145
        }
      );

      // Center subtitle
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(105, 115, 130);

      doc.text(
        "CASH PASSBOOK STATEMENT",
        pageWidth / 2,
        23,
        { align: "center" }
      );

      // Passbook status
      doc.setFontSize(9);

      doc.text(
        `Status: ${safeText(
          toInitCap(exportPassbook.status || "-")
        )}`,
        pageWidth / 2,
        29,
        { align: "center" }
      );

      // Applied date filter
      if (appliedFromDate || appliedToDate) {
        doc.setFontSize(8);

        doc.text(
          `Period: ${appliedFromDate || "Beginning"} to ${appliedToDate || "Present"
          }`,
          pageWidth / 2,
          35,
          { align: "center" }
        );
      }

      // RIGHT SIDE - Printed By
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(45, 55, 70);

      doc.text(
        `Printed By: ${printedBy}`,
        pageWidth - margin,
        15,
        {
          align: "right",
          maxWidth: 75
        }
      );

      // Print date
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(105, 115, 130);

      doc.text(
        `Date: ${printedDate}`,
        pageWidth - margin,
        22,
        { align: "right" }
      );

      // Print time
      doc.text(
        `Time: ${printedTime}`,
        pageWidth - margin,
        28,
        { align: "right" }
      );

      // Header separator
      doc.setDrawColor(220, 225, 232);
      doc.setLineWidth(0.3);

      doc.line(
        margin,
        40,
        pageWidth - margin,
        40
      );


      // ==========================================
      // SUMMARY BOXES
      // ==========================================
      const summary = [
        {
          title: "CASH IN",
          value: approvedCashIn,
          projected:
            `${pdfNumber(approvedCashIn)} + ` +
            `${pdfNumber(pendingCashIn)} = ` +
            pdfNumber(projectedIn),
          color: [16, 169, 135]
        },
        {
          title: "CASH OUT",
          value: approvedCashOut,
          projected:
            `${pdfNumber(approvedCashOut)} + ` +
            `${pdfNumber(pendingCashOut)} = ` +
            pdfNumber(projectedOut),
          color: [29, 120, 215]
        },
        {
          title: "NET BALANCE",
          value: netBalance,
          projected:
            `${pdfNumber(projectedIn)} - ` +
            `${pdfNumber(projectedOut)} = ` +
            pdfNumber(projectedBalance),
          color: [105, 65, 175]
        }
      ];

      const boxGap = 4;
      const boxWidth =
        (pageWidth - margin * 2 - boxGap * 2) / 3;

      const boxY = 44;
      const boxHeight = 35;

      summary.forEach((item, index) => {
        const x = margin + index * (boxWidth + boxGap);

        doc.setFillColor(...item.color);
        doc.roundedRect(
          x,
          boxY,
          boxWidth,
          boxHeight,
          3,
          3,
          "F"
        );

        doc.setTextColor(255, 255, 255);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(item.title, x + 5, boxY + 8);

        doc.setFontSize(17);
        doc.text(pdfAmount(item.value), x + 5, boxY + 17);

        doc.setDrawColor(255, 255, 255);
        doc.line(
          x + 5,
          boxY + 21,
          x + boxWidth - 5,
          boxY + 21
        );

        doc.setFontSize(8);
        doc.text(
          `Projected: ${item.projected}`,
          x + 5,
          boxY + 27,
          { maxWidth: boxWidth - 10 }
        );
      });

      doc.setTextColor(100, 110, 125);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(
        "Actual totals include approved transactions only.",
        margin,
        84
      );

      doc.text(
        "Projected totals include pending and rejected amounts, assuming approval.",
        margin,
        89
      );

      // ==========================================
      // TRANSACTION TABLE
      // ==========================================
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(35, 45, 65);
      doc.text("Transaction Details", margin, 99);

      const tableRows = allEntries.map((entry, index) => {
        const checker = entry.adminReviewedBy?.name
          ? `${entry.status === "rejectedByAdmin"
            ? "Rejected"
            : "Approved"
          } by ${entry.adminReviewedBy.name} (CashChecker)`
          : "";

        const accounts = entry.accountsReviewedBy?.name
          ? `${entry.status === "rejectedByAccounts"
            ? "Rejected"
            : "Approved"
          } by ${entry.accountsReviewedBy.name} (Accounts)`
          : "";

        const createdBy =
          entry.createdBy?.name ||
          entry.employee?.name ||
          exportPassbook.employee?.name ||
          "-";

        const details = [
          checker,
          accounts,
          entry.remark || "-",
          `By ${createdBy}`
        ]
          .filter(Boolean)
          .map(safeText)
          .join("\n");

        const documents = getCashDocuments(entry);

        return [
          String(index + 1),
          [
            safeText(entry.requestNumber || "-"),
            safeText(
              statusLabels[entry.status] ||
              entry.status ||
              "-"
            )
          ].join("\n"),
          pdfDate(entry.dateTime),
          details,
          entry.entryType === "in" ? "Cash In" : "Cash Out",
          safeText(entry.category?.name),
          safeText(toInitCap(entry.paymentMode || "-")),
          documents.length ? String(documents.length) : "-",
          pdfAmount(entry.amount),
          entry.balanceAfter == null
            ? "-"
            : pdfAmount(entry.balanceAfter),
          safeText(entry.vendor?.name)
        ];
      });

      autoTable(doc, {
        startY: 104,
        margin: {
          left: margin,
          right: margin,
          bottom: 15
        },
        head: [[
          "S.No",
          "Request No.",
          "Date & Time",
          "Details",
          "Type",
          "Category",
          "Mode",
          "Bills",
          "Amount",
          "Balance",
          "Vendor"
        ]],
        body: tableRows,
        theme: "grid",
        showHead: "everyPage",
        styles: {
          font: "helvetica",
          fontSize: 7,
          cellPadding: 2.5,
          overflow: "linebreak",
          valign: "middle",
          lineColor: [225, 230, 235],
          lineWidth: 0.15,
          textColor: [45, 55, 70]
        },
        headStyles: {
          fillColor: [38, 58, 83],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 7
        },
        alternateRowStyles: {
          fillColor: [246, 248, 251]
        },
        columnStyles: {
          0: { cellWidth: 11 },
          1: {
            cellWidth: 27,
            overflow: "linebreak",
            valign: "middle"
          },
          2: { cellWidth: 28 },
          3: { cellWidth: 62 },
          4: { cellWidth: 17 },
          5: { cellWidth: 28 },
          6: { cellWidth: 18 },
          7: { cellWidth: 12 },
          8: { cellWidth: 27 },
          9: { cellWidth: 27 },
          10: { cellWidth: 20 }
        },

        didDrawPage: () => {
          const currentPage = doc.internal.getNumberOfPages();
          const footerY = pageHeight - 7;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(130, 140, 150);

          // LEFT - Passbook name
          doc.text(
            safeText(exportPassbook.name),
            margin,
            footerY
          );

          // CENTER - Company system name
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);

          doc.text(
            "Generated From Indigo Infraprojects Pvt. Ltd. Store Management System",
            pageWidth / 2,
            footerY,
            {
              align: "center",
              maxWidth: 170
            }
          );

          // RIGHT - Page number
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);

          doc.text(
            `Page ${currentPage}`,
            pageWidth - margin,
            footerY,
            { align: "right" }
          );
        }

      });

      // ==========================================
      // SAVE PDF
      // ==========================================
      const safeFileName = String(
        exportPassbook.name || "Cash_Passbook"
      )
        .replace(/[^a-zA-Z0-9_-]+/g, "_")
        .slice(0, 80);

      doc.save(`${safeFileName}_Statement.pdf`);

    } catch (error) {
      showError(error, "Unable to download passbook PDF.");
    } finally {
      setPdfDownloading(false);
    }
  };

  const isFetching =
    storesLoading || passbooksLoading || detailsLoading;


  return (
    <div
      className="row"
      aria-busy={isFetching}
      style={{
        position: "relative",
        minHeight: "200px"
      }}
    >
      {isFetching && (
        <div
          role="status"
          aria-label="Loading cash passbook data"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 100,
            backgroundColor: "rgba(255, 255, 255, 0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "10px",
            cursor: "wait"
          }}
        >
          <Loader />
        </div>
      )}

      <div className="col-12">
        {!embedded && (
          <div className="filter cm-content-box box-primary cash-viewer-search-card">
            <div className="content-title">
              <div className="row g-2 align-items-center w-100 m-0">
                <div className="col-12 col-md-3">
                  <div className="cpa">
                    <i className="fa fa-book me-2" aria-hidden="true" />
                    Cash Passbook
                  </div>
                </div>

                <div className="col-12 col-md-6 text-md-center">
                  <span className="cash-viewer-search-hint">
                    Select a store and passbook to view details.
                  </span>
                </div>

                <div className="col-12 col-md-3 d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setSearchOpen((value) => !value)}
                    aria-expanded={searchOpen}
                    aria-controls="cash-passbook-search"
                  >
                    <i
                      className={`fa ${searchOpen ? "fa-chevron-up" : "fa-chevron-down"
                        } me-2`}
                      aria-hidden="true"
                    />
                    {searchOpen ? "Hide Search" : "Show Search"}
                  </button>
                </div>
              </div>
            </div>

            <Collapse in={searchOpen}>
              <div
                id="cash-passbook-search"
                className="cm-content-body form excerpt"
              >
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label htmlFor="viewer-store" className="form-label">
                        Store / Location
                      </label>

                      <Select
                        inputId="viewer-store"
                        options={storeOptions}
                        value={
                          storeOptions.find(
                            (option) => option.value === storeId
                          ) || null
                        }
                        onChange={changeStore}
                        placeholder="Select Store / Location"
                        isSearchable
                        isClearable
                        isLoading={storesLoading}
                        isDisabled={storesLoading}
                        noOptionsMessage={() => "No active stores available"}
                        styles={selectStyles}
                        className="custom-react-select"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor="viewer-passbook" className="form-label">
                        Passbook
                      </label>

                      <Select
                        inputId="viewer-passbook"
                        options={passbookOptions}
                        value={
                          passbookOptions.find(
                            (option) => option.value === passbookId
                          ) || null
                        }
                        onChange={changePassbook}
                        placeholder={
                          !storeId
                            ? "Select Store First"
                            : passbooksLoading
                              ? "Loading Passbooks..."
                              : "Select Passbook"
                        }
                        isSearchable
                        isClearable
                        isLoading={passbooksLoading}
                        isDisabled={!storeId || passbooksLoading}
                        noOptionsMessage={() =>
                          "No passbook available for this store"
                        }
                        styles={selectStyles}
                        className="custom-react-select"
                      />
                    </div>
                  </div>

                  {detailsError && (
                    <div className="alert alert-danger mt-3 mb-0">
                      {detailsError}
                    </div>
                  )}
                </div>
              </div>
            </Collapse>
          </div>
        )}

        {embedded && !passbook && detailsLoading && (
          <div className="text-center py-5" role="status">
            <span
              className="spinner-border spinner-border-sm me-2"
              aria-hidden="true"
            />
            Loading passbook...
          </div>
        )}

        {embedded && detailsError && (
          <div className="alert alert-danger" role="alert">
            {detailsError}
          </div>
        )}

        {passbook && (
          <div className="filter cm-content-box box-primary">
            <div className="content-title">
              <div className="row g-2 align-items-center w-100 m-0">
                <div className="col-12 col-md-4">
                  <div className="cpa">
                    <i className="fa fa-list me-2" aria-hidden="true" />
                    Passbook Transactions
                    <span className="badge bg-orange ms-2">
                      {pagination.total}
                    </span>
                  </div>
                </div>

                <div className="col-12 col-md-4 text-md-center">
                  <div className="cpa d-flex align-items-center justify-content-md-center flex-wrap gap-2">
                    <span style={{ overflowWrap: "anywhere" }}>
                      {toInitCap(passbook?.name || "—")}
                    </span>

                    {passbook?.status && (
                      <span
                        className={`badge ${passbook.status === "active"
                          ? "bg-success"
                          : "bg-secondary"
                          }`}
                        style={{
                          fontSize: "10px",
                          fontWeight: 600,
                          lineHeight: 1.2,
                          padding: "4px 8px",
                          borderRadius: "4px",
                          color: "#fff",
                          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.15)",
                          whiteSpace: "nowrap",
                          flexShrink: 0
                        }}
                      >
                        {toInitCap(passbook.status)}
                      </span>

                    )}

                    {/* PDF Download Icon */}
                    <button
                      type="button"
                      className="btn btn-link p-0 ms-2"
                      onClick={downloadPassbookPDF}
                      disabled={detailsLoading || pdfDownloading}
                      title="Download Passbook PDF"
                      aria-label="Download Passbook PDF"
                      style={{
                        color: "#dc3545",
                        fontSize: "21px",
                        lineHeight: 1,
                        textDecoration: "none",
                        cursor: pdfDownloading
                          ? "wait"
                          : "pointer"
                      }}
                    >
                      {pdfDownloading ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-label="Preparing PDF"
                        />
                      ) : (
                        <i
                          className="fas fa-file-pdf"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </div>
                </div>

                <div className="col-12 col-md-4 d-flex justify-content-end">

                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setDataOpen((value) => !value)}
                    aria-expanded={dataOpen}
                    aria-controls="cash-passbook-data"
                  >
                    <i
                      className={`fa ${dataOpen ? "fa-chevron-up" : "fa-chevron-down"
                        } me-2`}
                      aria-hidden="true"
                    />
                    {dataOpen ? "Hide Passbook" : "Show Passbook"}
                  </button>
                </div>
              </div>
            </div>

            <Collapse in={dataOpen}>
              <div
                id="cash-passbook-data"
                className="cm-content-body form excerpt"
              >
                <div className="card-body">
                  <div className="cash-viewer-summary cash-viewer-summary-three mb-3">
                    {balanceItems.map((item) => (
                      <div
                        key={item.key}
                        className={`cash-viewer-summary-box ${item.key === "in"
                          ? "cash-viewer-summary-green"
                          : item.key === "out"
                            ? "cash-viewer-summary-blue"
                            : "cash-viewer-summary-purple"
                          }`}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="cash-viewer-summary-icon"
                            aria-hidden="true"
                          >
                            {item.symbol}
                          </span>

                          <div style={{ minWidth: 0 }}>
                            <div className="cash-viewer-summary-value">
                              {getAmount(item.value).toLocaleString("en-IN", {
                                maximumFractionDigits: 2
                              })}
                            </div>

                            <div className="cash-viewer-summary-label mt-1">
                              {item.label}
                            </div>
                          </div>
                        </div>

                        <div className="cash-viewer-summary-note mt-2">
                          {"*** Totals include approved entries only."}
                        </div>

                        <div
                          className="mt-2"
                          style={{
                            borderTop: "1px solid rgba(255,255,255,0.30)",
                            paddingTop: "8px",
                            color: "#ffffff"
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#ffffff"
                              }}
                            >
                              Projected {item.label}
                            </span>

                            <span
                              style={{
                                fontSize: "17px",
                                fontWeight: 700,
                                lineHeight: 1.2,
                                color: "#ffffff",
                                overflowWrap: "anywhere"
                              }}
                            >
                              {detailsLoading
                                ? "Loading..."
                                : !projection
                                  ? "—"
                                  : item.projectionCalculation}
                            </span>
                          </div>

                          <div
                            style={{
                              marginTop: "5px",
                              fontSize: "9px",
                              lineHeight: 1.4,
                              color: "rgba(255,255,255,0.90)"
                            }}
                          >
                            {item.key === "balance"
                              ? "**** Projected Cash In − Projected Cash Out."
                              : "**** Approved + pending and rejected amounts, assuming approval."}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="row g-2 align-items-end cash-passbook-compact-filters cash-viewer-filter-panel">
                    <div className="col-12 col-md-6 col-xl-3">
                      <label htmlFor="viewer-search" className="form-label">
                        Search
                      </label>

                      <div className="input-group">
                        <input
                          id="viewer-search"
                          type="search"
                          className="form-control"
                          placeholder="Amount / CashMaker remark"
                          value={searchText}
                          maxLength={200}
                          disabled={detailsLoading}
                          onChange={(event) => {
                            const value = event.target.value;
                            setSearchText(value);

                            if (!value.trim()) {
                              setSearch("");
                              setPage(0);
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              setSearch(searchText.trim());
                              setPage(0);
                            }
                          }}
                        />

                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={detailsLoading}
                          onClick={() => {
                            setSearch(searchText.trim());
                            setPage(0);
                          }}
                          aria-label="Search transactions"
                        >
                          <i className="fa fa-search" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-xl-2">
                      <label htmlFor="viewer-category" className="form-label">
                        Category
                      </label>

                      <select
                        id="viewer-category"
                        className="form-control"
                        value={categoryId}
                        disabled={detailsLoading}
                        onChange={(event) => {
                          setCategoryId(event.target.value);
                          setPage(0);
                        }}
                      >
                        <option value="">All Categories</option>
                        {categoryFilters.map((category) => (
                          <option key={category._id} value={category._id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6 col-xl-2">
                      <label htmlFor="viewer-vendor" className="form-label">
                        Vendor
                      </label>

                      <select
                        id="viewer-vendor"
                        className="form-control"
                        value={vendorId}
                        disabled={detailsLoading}
                        onChange={(event) => {
                          setVendorId(event.target.value);
                          setPage(0);
                        }}
                      >
                        <option value="">All Vendors</option>
                        {vendorFilters.map((vendor) => (
                          <option key={vendor._id} value={vendor._id}>
                            {vendor.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6 col-xl-2">
                      <label
                        htmlFor="viewer-entry-type"
                        className="form-label"
                      >
                        Entry Type
                      </label>

                      <select
                        id="viewer-entry-type"
                        className="form-control"
                        value={entryType}
                        disabled={detailsLoading}
                        onChange={(event) => {
                          setEntryType(event.target.value);
                          setPage(0);
                        }}
                      >
                        <option value="">All Entries</option>
                        <option value="in">Cash In</option>
                        <option value="out">Cash Out</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-8 col-xl-2">
                      <label htmlFor="viewer-status" className="form-label">
                        Status
                      </label>

                      <select
                        id="viewer-status"
                        className="form-control"
                        value={status}
                        disabled={detailsLoading}
                        onChange={(event) => {
                          setStatus(event.target.value);
                          setPage(0);
                        }}
                      >
                        <option value="">All Statuses</option>
                        {Object.entries(statusLabels).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-4 col-xl-1">
                      <label
                        htmlFor="viewer-page-size"
                        className="form-label"
                      >
                        Rows
                      </label>

                      <div className="d-flex align-items-center gap-1">
                        <select
                          id="viewer-page-size"
                          className="form-control"
                          style={{ minWidth: 0, flex: "1 1 0" }}
                          value={pageSize}
                          disabled={detailsLoading}
                          onChange={(event) => {
                            setPageSize(Number(event.target.value));
                            setPage(0);
                          }}
                        >
                          {[10, 20, 50, 100].map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>


                        {Boolean(
                          searchText ||
                          search ||
                          categoryId ||
                          vendorId ||
                          entryType ||
                          status
                        ) && (

                            <button
                              type="button"
                              className="btn btn-outline-danger px-2 flex-shrink-0"
                              disabled={detailsLoading}
                              onClick={() => {
                                setSearchText("");
                                setSearch("");
                                setCategoryId("");
                                setVendorId("");
                                setEntryType("");
                                setStatus("");

                                setFromDate("");
                                setToDate("");
                                setAppliedFromDate("");
                                setAppliedToDate("");

                                setPage(0);
                              }}
                              title="Reset filters"
                              aria-label="Reset all transaction filters"
                            >
                              <i className="fa fa-times" aria-hidden="true" />
                            </button>
                          )}
                      </div>
                    </div>

                    {/* Duration Filter - Inside Existing Filter Panel */}
                    <div className="col-12 mt-2">
                      <div
                        className="d-flex flex-wrap justify-content-center align-items-end gap-2"
                        style={{
                          borderTop: "1px solid #e5e9f0",
                          paddingTop: "12px"
                        }}
                      >

                        {/* From Date */}
                        <div
                          style={{
                            width: "180px",
                            maxWidth: "100%"
                          }}
                        >
                          <label
                            htmlFor="viewer-from-date"
                            className="form-label"
                          >
                            From Date
                          </label>

                          <input
                            id="viewer-from-date"
                            type="date"
                            className="form-control"
                            value={fromDate}
                            max={toDate || undefined}
                            disabled={detailsLoading}
                            onChange={(event) =>
                              setFromDate(event.target.value)
                            }
                            style={{
                              height: "38px",
                              minHeight: "38px",
                              fontSize: "13px",
                              padding: "6px 10px",
                              borderRadius: "20px",
                              width: "100%"
                            }}
                          />
                        </div>

                        {/* To Date */}
                        <div
                          style={{
                            width: "180px",
                            maxWidth: "100%"
                          }}
                        >
                          <label
                            htmlFor="viewer-to-date"
                            className="form-label"
                          >
                            To Date
                          </label>

                          <input
                            id="viewer-to-date"
                            type="date"
                            className="form-control"
                            value={toDate}
                            min={fromDate || undefined}
                            disabled={detailsLoading}
                            onChange={(event) =>
                              setToDate(event.target.value)
                            }
                            style={{
                              height: "38px",
                              minHeight: "38px",
                              fontSize: "13px",
                              padding: "6px 10px",
                              borderRadius: "20px",
                              width: "100%"
                            }}
                          />
                        </div>

                        {/* Apply Button */}
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={
                              detailsLoading ||
                              (!fromDate && !toDate)
                            }
                            onClick={() => {
                              if (
                                fromDate &&
                                toDate &&
                                fromDate > toDate
                              ) {
                                Swal.fire(
                                  "Invalid Date Range",
                                  "From Date cannot be later than To Date.",
                                  "warning"
                                );
                                return;
                              }

                              setAppliedFromDate(fromDate);
                              setAppliedToDate(toDate);
                              setPage(0);
                            }}
                            style={{
                              height: "38px",
                              minWidth: "80px",
                              fontSize: "12px",
                              borderRadius: "20px",
                              padding: "6px 12px"
                            }}
                          >
                            <i className="fa fa-search me-1" />
                            Apply
                          </button>
                        </div>

                        {/* Clear Button - Show only when duration has value */}
                        {Boolean(
                          fromDate ||
                          toDate ||
                          appliedFromDate ||
                          appliedToDate
                        ) && (
                            <div>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                disabled={detailsLoading}
                                onClick={() => {
                                  setFromDate("");
                                  setToDate("");
                                  setAppliedFromDate("");
                                  setAppliedToDate("");
                                  setPage(0);
                                }}
                                style={{
                                  height: "38px",
                                  minWidth: "80px",
                                  fontSize: "12px",
                                  borderRadius: "20px",
                                  padding: "6px 12px"
                                }}
                              >
                                <i className="fa fa-times me-1" />
                                Clear
                              </button>
                            </div>
                          )}


                      </div>
                    </div>
                  </div>

                  <div className="table-responsive cash-viewer-table-panel">

                    <div
                      id="cashbook_content_wrapper"
                      className="dataTables_wrapper no-footer"
                    >
                      <div
                        style={{
                          overflowX: "auto",
                          overflowY: "auto",
                          maxHeight: "400px",
                          whiteSpace: "nowrap",
                          position: "relative",
                          zIndex: 0
                        }}
                      >
                        <table
                          id="cashbook-transactions-data"
                          className="table table-bordered table-responsive-lg table-striped table-condensed flip-content"
                        >
                          <thead className="cash-viewer-table-header">
                            <tr>
                              <th>S.No</th>
                              <th className="text-nowrap">Request No.</th>
                              <th>Date & Time</th>
                              <th>Details</th>
                              <th>Type</th>
                              <th>Category</th>
                              <th>Mode</th>
                              <th>Bills</th>
                              <th>Amount</th>
                              <th>Balance</th>
                              <th>Vendor</th>
                            </tr>
                          </thead>

                          <tbody>
                            {detailsLoading ? null : entries.length === 0 ? (
                              <tr>
                                <td colSpan={11} className="text-center py-4">
                                  No transactions found.
                                </td>
                              </tr>
                            ) : (
                              entries.map((entry, index) => {
                                const documents = getCashDocuments(entry);

                                return (
                                  <tr key={entry._id}>
                                    <td>{page * pageSize + index + 1}</td>


                                    <td className="text-nowrap">
                                      <div className="d-flex flex-column align-items-start gap-1">
                                        <span className="fw-semibold text-primary">
                                          {entry.requestNumber || "—"}
                                        </span>

                                        <span
                                          className={`badge ${statusColors[entry.status] || "bg-secondary"
                                            }`}
                                          style={{
                                            fontSize: "10px",
                                            fontWeight: 500,
                                            padding: "4px 7px",
                                            borderRadius: "4px"
                                          }}
                                        >
                                          {statusLabels[entry.status] || entry.status || "—"}
                                        </span>
                                      </div>
                                    </td>


                                    <td className="text-nowrap">
                                      {(() => {
                                        const formatted =
                                          formatDate(entry.dateTime);

                                        if (!formatted) return "—";

                                        return (
                                          <div className="cash-entry-date-time">
                                            <div>{formatted.date}</div>
                                            <div className="cash-entry-time">
                                              {formatted.time}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </td>

                                    <td
                                      className="cash-transaction-details-cell cash-entry-details-clickable"
                                      onClick={() => setSelectedEntry(entry)}
                                    >
                                      <div className="cash-transaction-details">
                                        {entry.adminReviewedBy?.name && (
                                          <div className="cash-transaction-details-meta">
                                            {entry.status === "rejectedByAdmin"
                                              ? "Rejected By:"
                                              : "Approved By:"}{" "}
                                            {entry.adminReviewedBy.name}{" "}
                                            (CashChecker)
                                          </div>
                                        )}

                                        {entry.accountsReviewedBy?.name && (
                                          <div className="cash-transaction-details-meta">
                                            {entry.status === "rejectedByAccounts"
                                              ? "Rejected By:"
                                              : "Approved By:"}{" "}
                                            {entry.accountsReviewedBy.name}{" "}
                                            (Accounts)
                                          </div>
                                        )}

                                        <div
                                          className="cash-transaction-details-remark"
                                          title={entry.remark || ""}
                                        >
                                          {entry.remark || "—"}
                                        </div>

                                        <div className="cash-transaction-details-meta">
                                          by{" "}
                                          {entry.createdBy?.name ||
                                            entry.employee?.name ||
                                            passbook?.employee?.name ||
                                            "—"}
                                        </div>
                                      </div>

                                      <button
                                        type="button"
                                        className="cash-entry-details-open-button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setSelectedEntry(entry);
                                        }}
                                        aria-label={`View entry details for ${entry.voucherNo || "cash entry"
                                          }`}
                                      >
                                        View Details
                                      </button>
                                    </td>

                                    <td>
                                      <span
                                        className={`badge ${entry.entryType === "in"
                                          ? "bg-success"
                                          : "bg-warning text-dark"
                                          }`}
                                      >
                                        {entry.entryType === "in"
                                          ? "Cash In"
                                          : "Cash Out"}
                                      </span>
                                    </td>

                                    <td className="scrollable-description">
                                      {entry.category?.name || "—"}
                                    </td>

                                    <td>
                                      {toInitCap(entry.paymentMode || "—")}
                                    </td>

                                    {/* Bills / Documents */}
                                    <td className="text-nowrap">
                                      {documents.length > 0 ? (
                                        <div className="d-flex align-items-center gap-1">
                                          {documents.map((file, fileIndex) => (
                                            <button
                                              key={`${file.url}-${fileIndex}`}
                                              type="button"
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                setSelectedDocumentIndex(
                                                  fileIndex
                                                );
                                                setSelectedBills(entry);
                                              }}
                                              title={`View attachment ${fileIndex + 1
                                                }`}
                                              aria-label={`View attachment ${fileIndex + 1
                                                }`}
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "5px",
                                                padding: "4px 7px",
                                                borderRadius: "4px",
                                                fontSize: "12px"
                                              }}
                                            >
                                              <i
                                                className="fa fa-paperclip"
                                                aria-hidden="true"
                                              />
                                              <span>{fileIndex + 1}</span>
                                            </button>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </td>

                                    <td className="text-nowrap">
                                      {formatAmount(entry.amount)}
                                    </td>

                                    <td className="text-nowrap">
                                      {formatEntryBalance(entry.balanceAfter)}
                                    </td>
                                    <td className="scrollable-description">
                                      <div>{entry.vendor?.name || "—"}</div>

                                      {entry.vendor?.mobileNo && (
                                        <div className="small text-muted mt-1">
                                          {entry.vendor.mobileNo}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>

                      {!detailsLoading && (
                        <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                          <div className="dataTables_info">
                            {pagination.total > 0 ? (
                              <>
                                Showing {page * pageSize + 1} to{" "}
                                {Math.min(
                                  pagination.total,
                                  page * pageSize + entries.length
                                )}{" "}
                                of {pagination.total} entries
                              </>
                            ) : (
                              <>Showing 0 to 0 of 0 entries</>
                            )}
                          </div>

                          {pagination.total > 0 && (
                            <Pagination
                              activePage={page}
                              totalPages={pagination.totalPages}
                              onPageChange={(nextPage) => {
                                if (
                                  detailsLoading ||
                                  !Number.isInteger(nextPage)
                                ) {
                                  return;
                                }

                                setPage(
                                  Math.max(
                                    0,
                                    Math.min(
                                      nextPage,
                                      pagination.totalPages - 1
                                    )
                                  )
                                );
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        )}
      </div>

      <CashEntryDetailsModal
        entry={selectedEntry}
        passbook={passbook}
        handleClose={() => setSelectedEntry(null)}
        onChanged={() =>
          setWorkflowRefresh((previous) => previous + 1)
        }
      />

      <CashDocumentPreviewModal
        show={Boolean(selectedBills)}
        handleClose={() => {
          setSelectedBills(null);
          setSelectedDocumentIndex(0);
        }}
        entry={selectedBills}
        initialIndex={selectedDocumentIndex}
      />
    </div>
  );
};

export default CashPassbookViewer;